/**
 * useWorkspaceClient — Central hook for workspace-scoped data access.
 *
 * For client-role users: locks to their assigned client (profile.client_id).
 * For admin-role users: uses DashboardContext.selectedClient (supports "all" / switching).
 *
 * Returns a ClientFilters object with the correct filter value for each data table,
 * since tables reference clients inconsistently:
 *   - campaigns.client_id  → text (clients.id)
 *   - mailboxes.client_id  → integer (clients.client_id / SmartLead ID)
 *   - domains.client_name  → text (clients.name)
 *   - masterinbox_replies.workspace_name → text (clients.name)
 *   - blocklist.workspace_name → text (clients.name)
 */

import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useDashboard } from '@/contexts/DashboardContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface ClientRecord {
  id: string        // clients.id (text PK — UUID or numeric string)
  name: string      // clients.name (e.g. "Cruva", "Duel")
  client_id: number // clients.client_id (bigint, SmartLead ID)
}

export interface ClientFilters {
  /** The resolved client. null if "all clients" (admin Master View). */
  client: ClientRecord | null
  /** Whether data is scoped to a single client */
  isScoped: boolean
  /** Whether the user is a client-role user (locked scope) */
  isClientUser: boolean
  /** Filter for campaigns.client_id (text — clients.id) */
  campaignClientId: string | undefined
  /** Filter for mailboxes.client_id (integer — SmartLead ID) */
  mailboxClientId: number | undefined
  /** Filter for domains.client_name (text — clients.name) */
  domainClientName: string | undefined
  /** Filter for masterinbox_replies.workspace_name / blocklist.workspace_name */
  workspaceName: string | undefined
  /** Loading state */
  loading: boolean
}

/**
 * Fetches all active clients once and caches them.
 * Used to resolve client IDs to full client records.
 */
function useClientRecords() {
  return useQuery({
    queryKey: ['workspace-clients'],
    queryFn: async (): Promise<ClientRecord[]> => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, client_id')
        .eq('status', 'Active')
        .order('name')

      if (error) throw error
      return (data || []).map(c => ({
        id: c.id,
        name: c.name,
        client_id: Number(c.client_id),
      }))
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useWorkspaceClient(): ClientFilters {
  const { profile, isClient } = useAuth()
  const { state } = useDashboard()
  const { data: clients, isLoading } = useClientRecords()

  return useMemo(() => {
    // Determine the effective client ID
    // Client users: always locked to their profile.client_id
    // Admin users: uses DashboardContext.selectedClient
    const effectiveClientId = isClient
      ? profile?.client_id ?? null
      : state.selectedClient === 'all'
        ? null
        : state.selectedClient

    // If no specific client selected (admin Master View), return unscoped
    if (!effectiveClientId || !clients) {
      return {
        client: null,
        isScoped: false,
        isClientUser: isClient,
        campaignClientId: undefined,
        mailboxClientId: undefined,
        domainClientName: undefined,
        workspaceName: undefined,
        loading: isLoading,
      }
    }

    // Resolve the client record — try matching by id first, then by name, then by client_id
    const client = clients.find(c => c.id === effectiveClientId)
      || clients.find(c => c.name === effectiveClientId)
      || clients.find(c => String(c.client_id) === effectiveClientId)
      || null

    if (!client) {
      console.warn(`[useWorkspaceClient] Could not resolve client for ID: ${effectiveClientId}`)
      return {
        client: null,
        isScoped: false,
        isClientUser: isClient,
        campaignClientId: undefined,
        mailboxClientId: undefined,
        domainClientName: undefined,
        workspaceName: undefined,
        loading: isLoading,
      }
    }

    return {
      client,
      isScoped: true,
      isClientUser: isClient,
      campaignClientId: client.id,
      mailboxClientId: client.client_id,
      domainClientName: client.name,
      workspaceName: client.name,
      loading: false,
    }
  }, [isClient, profile?.client_id, state.selectedClient, clients, isLoading])
}
